import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Copy, Download, KeyRound } from "lucide-react";
import { ApiError, errorMessage, fieldErrors, post } from "@/api/client";
import { Button, Input, useToast } from "@/ui";
import logo from "@/logo-mark.svg";
import { meQueryKey } from "./AuthProvider";
import "./auth.css";

type Stage = "totp" | "enroll_totp" | "done";

function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-brand"><img src={logo} alt="" />PeriFlow Admin</div>
        <h1>{title}</h1>
        {children}
      </div>
    </main>
  );
}

function useStageNavigation() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (stage: Stage) => {
    if (stage === "totp") navigate("/login/verify");
    else if (stage === "enroll_totp") navigate("/login/enroll");
    else {
      qc.invalidateQueries({ queryKey: meQueryKey });
      navigate("/", { replace: true });
    }
  };
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const go = useStageNavigation();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await post<{ stage: Stage }>("/auth/login", { email, password });
      go(res.stage);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Sign in">
      <form className="stack" onSubmit={submit}>
        <Input label="Work email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
        <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <Button variant="primary" type="submit" loading={busy}>Continue</Button>
        <p className="subtle" style={{ fontSize: 12 }}>Forgot your password or lost your authenticator? Ask an admin for a reset link.</p>
      </form>
    </AuthCard>
  );
}

export function VerifyPage() {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const go = useStageNavigation();
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await post("/auth/totp/verify", recovery ? { recovery_code: code.trim() } : { code: code.replace(/\s/g, "") });
      go("done");
    } catch (err) {
      if (err instanceof ApiError && err.code === "unauthenticated") navigate("/login");
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Two-step verification">
      <form className="stack" onSubmit={submit}>
        <p className="muted">{recovery ? "Enter one of your recovery codes. Each works once." : "Enter the 6-digit code from your authenticator app."}</p>
        <Input label={recovery ? "Recovery code" : "Code"} value={code} onChange={(e) => setCode(e.target.value)} autoFocus required
          inputMode={recovery ? "text" : "numeric"} autoComplete="one-time-code" />
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <Button variant="primary" type="submit" loading={busy}>Verify</Button>
        <Button variant="ghost" size="sm" onClick={() => { setRecovery(!recovery); setCode(""); setError(""); }}>
          {recovery ? "Use authenticator code" : "Use a recovery code"}
        </Button>
      </form>
    </AuthCard>
  );
}

export function EnrollPage() {
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const go = useStageNavigation();
  const toast = useToast();

  useEffect(() => {
    post<{ secret: string; otpauth_url: string }>("/auth/totp/enroll")
      .then(async (r) => {
        setSecret(r.secret);
        setQr(await QRCode.toDataURL(r.otpauth_url, { margin: 1, width: 196 }));
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await post<{ recovery_codes: string[] }>("/auth/totp/confirm", { code: code.replace(/\s/g, "") });
      setCodes(r.recovery_codes);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    const text = "PeriFlow Admin recovery codes\n\n" + codes.join("\n") + "\n\nEach code works once.";
    return (
      <AuthCard title="Save your recovery codes">
        <p className="muted">If you lose your phone, each of these codes lets you sign in once. They won't be shown again.</p>
        <div className="codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>
        <div className="row">
          <Button icon={Copy} onClick={() => navigator.clipboard.writeText(text).then(() => toast("Copied"))}>Copy</Button>
          <Button icon={Download} onClick={() => {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
            a.download = "periflow-recovery-codes.txt";
            a.click();
          }}>Download</Button>
        </div>
        <Button variant="primary" onClick={() => go("done")}>I've saved them</Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set up two-step verification">
      <p className="muted">Scan this with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the code it shows.</p>
      {qr ? <img className="qr" src={qr} alt="QR code for your authenticator app" width={196} height={196} /> : <div className="qr" style={{ width: 196, height: 196 }} />}
      {secret && <details><summary className="subtle">Can't scan? Enter this key</summary><p className="secret">{secret}</p></details>}
      <form className="stack" onSubmit={submit}>
        <Input label="Code from the app" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <Button variant="primary" type="submit" loading={busy} disabled={!secret}>Turn on</Button>
      </form>
    </AuthCard>
  );
}

// The invite token lives in the URL fragment, which browsers never send to servers, so it does
// not appear in any access log. It is read once and removed from the address bar.
export function AcceptInvitePage() {
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get("token") ?? "");
  const [info, setInfo] = useState<{ email: string; display_name: string; purpose: string; expires_at: string } | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(token ? "" : "This link is incomplete. Open the full link you were sent.");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const go = useStageNavigation();

  useEffect(() => {
    history.replaceState(null, "", location.pathname);
    if (!token) return;
    post<typeof info>("/auth/token/inspect", { token }).then(setInfo).catch((e) => setError(errorMessage(e)));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setFields({ confirm: "The passwords don't match." });
      return;
    }
    setBusy(true);
    setError("");
    setFields({});
    try {
      const r = await post<{ stage: Stage }>("/auth/token/accept", { token, password });
      go(r.stage);
    } catch (err) {
      setFields(fieldErrors(err));
      if (!Object.keys(fieldErrors(err)).length) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = info?.purpose === "reset";
  return (
    <AuthCard title={reset ? "Reset your access" : "Welcome to the team"}>
      {info ? (
        <form className="stack" onSubmit={submit}>
          <p className="muted">
            {reset ? "Choose a new password for " : `Hi ${info.display_name} — choose a password for `}<strong>{info.email}</strong>.
          </p>
          <Input label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
            hint="At least 12 characters. A few unrelated words make a strong, memorable password." error={fields.password} autoFocus required minLength={12} />
          <Input label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={fields.confirm} required />
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          <Button variant="primary" type="submit" loading={busy} icon={KeyRound}>Set password</Button>
          <p className="subtle" style={{ fontSize: 12 }}>Next you'll set up two-step verification. This link expires {new Date(info.expires_at).toLocaleString()}.</p>
        </form>
      ) : error ? (
        <div className="alert alert-danger" role="alert">{error}</div>
      ) : (
        <p className="muted">Checking your link…</p>
      )}
    </AuthCard>
  );
}
